/**
 * Credit Block Subscription React Query hooks
 * Fetches CreditBlock{suffix} per client for the Subscriptions tab.
 * Client → CreditBlock graph traversal only (credit_block_client_rel); no bulk list fallback.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import type { CreditBlockProduct } from '../../types';
import { mapCreditBlockRecordToProduct } from './creditBlockProductMapper';
import { getCreditBlocksViaTraversalFromClient } from './creditBlockQueries';
import { updateRecord } from '../recordStorageService';
import { resolveGraphUserNamesBySfIds, looksLikeGraphUserSfId } from '../gobodo/gobodoUserSfIdLookup';

const CUSTOM_SUFFIX = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';

/**
 * Post-process subscriptions: resolve SF user IDs to display names in createdBy / lastModifiedBy.
 */
async function resolveUserNamesOnSubscriptions(subs: CreditBlockProduct[]): Promise<void> {
  const sfIds = new Set<string>();
  for (const s of subs) {
    if (looksLikeGraphUserSfId(s.createdBy)) sfIds.add(s.createdBy!);
    if (looksLikeGraphUserSfId(s.lastModifiedBy)) sfIds.add(s.lastModifiedBy!);
  }
  if (sfIds.size === 0) return;

  try {
    const nameMap = await resolveGraphUserNamesBySfIds(Array.from(sfIds));
    for (const s of subs) {
      const createdName = s.createdBy ? nameMap.get(s.createdBy) : undefined;
      if (createdName) s.createdBy = createdName;

      const modifiedName = s.lastModifiedBy ? nameMap.get(s.lastModifiedBy) : undefined;
      if (modifiedName) s.lastModifiedBy = modifiedName;
    }
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[CreditBlockSubscriptions] Failed to resolve user names:', err);
    }
  }
}

export function useCreditBlockSubscriptions(
  clientId: string,
  enabled = true,
  placeholderData?: CreditBlockProduct[]
) {
  return useQuery({
    queryKey: ['credit-block', 'subscriptions', clientId, CUSTOM_SUFFIX],
    placeholderData,
    queryFn: async (): Promise<CreditBlockProduct[]> => {
      let forClient: Record<string, unknown>[] = [];
      try {
        forClient = await getCreditBlocksViaTraversalFromClient(clientId);
      } catch {
        forClient = [];
      }
      const products = forClient.map(mapCreditBlockRecordToProduct);
      await resolveUserNamesOnSubscriptions(products);
      return products;
    },
    enabled: USE_GOBODO_API && !!clientId && enabled,
    staleTime: 60_000,
  });
}

type UpdateCreditBlockSubscriptionInput = {
  clientId: string;
  subscription: CreditBlockProduct;
  updates: Record<string, unknown>;
};

const resolveSubscriptionRecordId = (sub: CreditBlockProduct): string => {
  const fromKey = String(sub.recordKey ?? '').trim();
  if (fromKey) return fromKey;

  const fromRecordId = String(sub.recordId ?? '').trim();
  if (fromRecordId) return fromRecordId;

  const fromDisplayId = String(sub.id ?? '').trim();
  if (fromDisplayId && !/^CB-SUB-/i.test(fromDisplayId)) return fromDisplayId;

  const cbSubMatch = fromDisplayId.match(/^CB-SUB-(.+)$/i);
  if (cbSubMatch?.[1]) return cbSubMatch[1];

  throw new Error('Unable to resolve Credit Block subscription record id');
};

export function useUpdateCreditBlockSubscriptionField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ clientId, subscription, updates }: UpdateCreditBlockSubscriptionInput) => {
      const recordId = resolveSubscriptionRecordId(subscription);
      return updateRecord(`CreditBlock${CUSTOM_SUFFIX}`, recordId, updates, { gobodoPropertyPatch: true });
    },
    onSuccess: (_updated, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['credit-block', 'subscriptions', variables.clientId, CUSTOM_SUFFIX],
      });
    },
  });
}
